import type { RecordedRun } from '../contracts/game'
import type {
  BoardId,
  LeaderboardPage,
  OnlineResult,
  OnlineServices,
  RoomJoinInfo,
  ServiceStatus,
} from './OnlineServices'

const IDENTITY_KEY = 'neon-descent-online-identity'

interface StoredIdentity {
  readonly token: string
  readonly callsign: string
  readonly expiresAt: number
}

export interface HttpOnlineOptions {
  readonly fetcher?: typeof fetch
  readonly storage?: Storage | null
  readonly callsign?: string
}

function isIdentity(value: unknown): value is StoredIdentity {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<StoredIdentity>
  return typeof candidate.token === 'string'
    && typeof candidate.callsign === 'string'
    && typeof candidate.expiresAt === 'number'
    && candidate.expiresAt > Date.now() + 60_000
}

function unavailable<T>(reason: 'offline' | 'quota' | 'error' = 'error'): OnlineResult<T> {
  return { ok: false, reason }
}

function withSignal(signal?: AbortSignal): Pick<RequestInit, 'signal'> | Record<string, never> {
  return signal === undefined ? {} : { signal }
}

export class HttpOnlineServices implements OnlineServices {
  private readonly baseUrl: string
  private readonly fetcher: typeof fetch
  private readonly storage: Storage | null
  private readonly requestedCallsign: string
  private identity: StoredIdentity | null = null

  public constructor(baseUrl: string, options: HttpOnlineOptions = {}) {
    this.baseUrl = baseUrl.replace(/\/$/, '')
    this.fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis)
    this.storage = options.storage === undefined
      ? (typeof localStorage === 'undefined' ? null : localStorage)
      : options.storage
    this.requestedCallsign = options.callsign ?? 'RUNNER'
    this.identity = this.loadIdentity()
  }

  public async getStatus(signal?: AbortSignal): Promise<ServiceStatus> {
    try {
      const response = await this.fetcher(`${this.baseUrl}/api/status`, withSignal(signal))
      return response.ok ? { available: true } : { available: false, reason: 'error' }
    } catch {
      return { available: false, reason: typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'error' }
    }
  }

  public async getLeaderboard(board: BoardId, cursor?: string, signal?: AbortSignal): Promise<LeaderboardPage> {
    try {
      const url = new URL(`${this.baseUrl}/api/leaderboards/${board}`)
      if (cursor !== undefined) url.searchParams.set('cursor', cursor)
      const response = await this.fetcher(url, withSignal(signal))
      if (!response.ok) return { entries: [], stale: true }
      const value = await response.json() as LeaderboardPage
      return {
        entries: Array.isArray(value.entries) ? value.entries.slice(0, 50) : [],
        stale: value.stale === true,
        ...(typeof value.nextCursor === 'string' ? { nextCursor: value.nextCursor } : {}),
      }
    } catch {
      return { entries: [], stale: true }
    }
  }

  public async submitRun(run: RecordedRun, signal?: AbortSignal): Promise<OnlineResult<{ readonly rank: number }>> {
    const identity = await this.ensureIdentity(signal)
    if (identity === null) return unavailable(this.offlineReason())
    try {
      const response = await this.fetcher(`${this.baseUrl}/api/runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${identity.token}` },
        body: JSON.stringify({ callsign: identity.callsign, run }),
        ...withSignal(signal),
      })
      if (response.status === 429) return unavailable('quota')
      if (!response.ok) return unavailable('error')
      const value = await response.json() as { rank?: unknown }
      return typeof value.rank === 'number'
        ? { ok: true, value: { rank: value.rank } }
        : unavailable('error')
    } catch {
      return unavailable(this.offlineReason())
    }
  }

  public async createRoom(signal?: AbortSignal): Promise<OnlineResult<RoomJoinInfo>> {
    return this.roomRequest('/api/rooms', { method: 'POST', ...withSignal(signal) })
  }

  public async joinRoom(code: string, signal?: AbortSignal): Promise<OnlineResult<RoomJoinInfo>> {
    return this.roomRequest(`/api/rooms/${encodeURIComponent(code.toUpperCase())}`, withSignal(signal))
  }

  private async roomRequest(path: string, init: RequestInit): Promise<OnlineResult<RoomJoinInfo>> {
    try {
      const response = await this.fetcher(`${this.baseUrl}${path}`, init)
      if (response.status === 429) return unavailable('quota')
      if (!response.ok) return unavailable('error')
      const value = await response.json() as Partial<RoomJoinInfo>
      if (typeof value.roomId !== 'string' || typeof value.code !== 'string' || typeof value.socketUrl !== 'string') {
        return unavailable('error')
      }
      const socket = new URL(value.socketUrl, this.baseUrl)
      socket.protocol = socket.protocol === 'https:' ? 'wss:' : 'ws:'
      return { ok: true, value: { roomId: value.roomId, code: value.code, socketUrl: socket.toString() } }
    } catch {
      return unavailable(this.offlineReason())
    }
  }

  private async ensureIdentity(signal?: AbortSignal): Promise<StoredIdentity | null> {
    if (this.identity !== null && this.identity.expiresAt > Date.now() + 60_000) return this.identity
    try {
      const response = await this.fetcher(`${this.baseUrl}/api/identity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callsign: this.requestedCallsign }),
        ...withSignal(signal),
      })
      if (!response.ok) return null
      const value: unknown = await response.json()
      if (!isIdentity(value)) return null
      this.identity = value
      try { this.storage?.setItem(IDENTITY_KEY, JSON.stringify(value)) } catch { /* private mode */ }
      return value
    } catch {
      return null
    }
  }

  private loadIdentity(): StoredIdentity | null {
    try {
      const raw = this.storage?.getItem(IDENTITY_KEY)
      if (raw === null || raw === undefined) return null
      const value: unknown = JSON.parse(raw)
      return isIdentity(value) ? value : null
    } catch {
      return null
    }
  }

  private offlineReason(): 'offline' | 'error' {
    return typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'error'
  }
}

export function createConfiguredOnlineServices(): OnlineServices | null {
  const baseUrl = import.meta.env.VITE_API_BASE_URL
  return typeof baseUrl === 'string' && baseUrl.length > 0 ? new HttpOnlineServices(baseUrl) : null
}
